<script lang="ts">
  import { browser } from '$app/environment';

  import type { EquipmentPanelProps } from '$lib/types/layoutTypes';

  import Carousel from 'svelte-carousel';
  import ArrowButton from '../control/ArrowButton.svelte';
  import PicturePanel from './PicturePanel.svelte';
  import Viewport from '../utility/Viewport.svelte';
  import { richTextFormatter } from '$lib/helpers/richTextHelper';
  import { page } from '$app/stores';

  export let equipmentPanel: EquipmentPanelProps;
  export let sectionClass: string = '';
  // 1216 is the max width of the container normally
  export let maxContainerWidth: number = 1216;
  export let eyelineTextOveride: string = null;
  export let bodyWidth: string;
  let path;
  $: path = $page.url.pathname;

  const headingText: string = 'What you’ll need';
  const itemCount: number = equipmentPanel?.equipmentCollection?.items?.length || 0;
  const DESKTOP_ITEM_WIDTH = 176; //Px
  const MOBILE_ITEM_WIDTH = 128; //Px
  const DESKTOP_ITEM_GAP = 32; //Px
  const MOBILE_ITEM_GAP = 16; //Px
  const DESKTOP_PADDING = 0; //Px
  const MOBILE_PADDING = 16; //Px

  let carousel: any;
  let width: number = 0;
  let max_items: number = 1.5;
  let pageIndex = 0;

  $: carouselWidth = width >= maxContainerWidth ? maxContainerWidth : width;

  // Calculates the movement of the last item in the carousel
  const isAtEnd = (pageIndex) => pageIndex >= itemCount - Math.floor(max_items);
  $: position = isAtEnd(pageIndex) ? 'last' : null;

  // 640 is the sm breakpoint
  $: if (carouselWidth >= 640) {
    max_items = carouselWidth / (DESKTOP_ITEM_WIDTH + DESKTOP_ITEM_GAP - DESKTOP_PADDING);
  } else {
    max_items = carouselWidth / (MOBILE_ITEM_WIDTH + MOBILE_ITEM_GAP + MOBILE_PADDING);
  }

  $: if ($page.url.href.includes('blogs') || $page.url.href.includes('programs')) {
    bodyWidth = 'max-w-800';
  } else {
    bodyWidth = 'max-w-1280';
  }

  const handleNextClick = () => {
    carousel.goToNext();
  };
  const handlePrevClick = () => {
    carousel.goToPrev();
  };
</script>

<section class={sectionClass}>
  <Viewport bind:width />
  <div class="flex flex-col justify-center items-center text-center pb-8 lg:pb-12">
    <p class="font-semibold text-primary-700 text-sm sm:text-base">
      {#if !eyelineTextOveride}
        {itemCount}
        {itemCount > 1 ? 'Items' : 'Item'}
      {/if}
      {#if eyelineTextOveride}
        Equipment
      {/if}
    </p>
    <h2 class="font-semibold text-gray-900 my-2 sm:my-3 text-30 sm:text-display-md">
      {headingText}
    </h2>
    {#if equipmentPanel?.bodyText}
      <p class="font-medium text-gray-600 text-md lg:text-lg {bodyWidth}">
        {equipmentPanel.bodyText}
      </p>
    {/if}
    {#if equipmentPanel?.bodyRichText}
      <p class="font-medium text-gray-600 {bodyWidth}">
        {@html richTextFormatter(equipmentPanel.bodyRichText)}
      </p>
    {/if}
  </div>
  {#if browser && itemCount > max_items}
    <div class="w-[calc(100%+1rem)]" {position}>
      {#key max_items}
        <Carousel
          bind:this={carousel}
          arrows={false}
          dots={false}
          infinite={false}
          particlesToShow={max_items}
          particlesToScroll={1}
          on:pageChange={(event) => (pageIndex = event.detail)}
        >
          {#if equipmentPanel?.equipmentCollection?.items?.length}
            {#each equipmentPanel?.equipmentCollection?.items as equipmentItem}
              <div class="carousel-item">
                <PicturePanel
                  {...equipmentItem}
                  captionClass="font-semibold text-center text-gray-900 text-md sm:text-xl"
                  containerClass="grid grid-flow-row gap-3 lg:gap-6 w-full max-w-[8rem] sm:max-w-[11rem]"
                  imgClass="max-w-44 rounded-xl"
                  pictureClass="aspect-square bg-gray-200 rounded-xl"
                />
              </div>
            {/each}
          {/if}
        </Carousel>
      {/key}

      <div class="justify-center items-start gap-8 flex mt-7 sm:mt-12 flex-row w-full">
        {#if pageIndex == 0}
          <ArrowButton direction="left" arrowStyle="pointer-events-none opacity-50" />
        {:else}
          <ArrowButton direction="left" on:click={handlePrevClick} />
        {/if}
        {#if position == 'last'}
          <ArrowButton direction="right" arrowStyle="pointer-events-none opacity-50" />
        {:else}
          <ArrowButton direction="right" on:click={handleNextClick} />
        {/if}
      </div>
    </div>
  {:else}
    <div class="flex flex-row gap-4 justify-center items-baseline sm:gap-8">
      {#if equipmentPanel?.equipmentCollection?.items?.length}
        {#each equipmentPanel?.equipmentCollection?.items as equipmentItem}
          <div class="">
            <PicturePanel
              {...equipmentItem}
              captionClass="font-semibold text-center text-gray-900 text-md sm:text-xl"
              containerClass="grid grid-flow-row gap-3 lg:gap-6 w-full max-w-[8rem] sm:max-w-[11rem]"
              imgClass="max-w-44 rounded-xl"
              pictureClass="aspect-square bg-gray-200 rounded-xl"
            />
          </div>
        {/each}
      {/if}
    </div>
  {/if}
</section>

<style>
  .carousel-item {
    transition: margin 400ms ease-in-out;
  }
  .carousel-container[position='last'] .carousel-item:first-child {
    margin-left: -30%;
  }

  @media (min-width: 640px) {
    .carousel-container[position='last'] .carousel-item:first-child {
      margin-left: -15%;
    }
  }
</style>
